import React, { useEffect, useRef } from 'react';
import { motion } from 'motion/react';

import { ChatTranscript } from '@/components/livekit/app/chat-transcript';
import { PreConnectMessage } from '@/components/livekit/app/preconnect-message';
import {
  AgentControlBar,
  type ControlBarControls,
} from '@/components/livekit/livekit/agent-control-bar/agent-control-bar';
import { SheetFooter } from '@/components/ui/sheet';

import { useChatMessages } from '@/hooks/useChatMessages';
import { useConnectionTimeout } from '@/hooks/useConnectionTimeout';
import { useDebugMode } from '@/hooks/useDebug';

import { cn } from '@/lib/utils';

import { ScrollArea } from '../livekit/scroll-area/scroll-area';

import { useLivekitUiStore } from '@/store/livekit-ui.store';
import type { LivekitConfig } from '@/types/livekit.type';

const MotionBottom = motion.create('div');

const IN_DEVELOPMENT = process.env.NODE_ENV !== 'production';
const BOTTOM_VIEW_MOTION_PROPS = {
  variants: {
    visible: {
      opacity: 1,
      translateY: '0%',
    },
    hidden: {
      opacity: 0,
      translateY: '100%',
    },
  },
  initial: 'hidden',
  animate: 'visible',
  exit: 'hidden',
  transition: {
    duration: 0.3,
    delay: 0.5,
    ease: 'easeOut',
  },
};

interface FadeProps {
  top?: boolean;
  bottom?: boolean;
  className?: string;
}

export function Fade({ top = false, bottom = false, className }: FadeProps) {
  return (
    <div
      className={cn(
        'from-background pointer-events-none h-4 bg-linear-to-b to-transparent',
        top && 'bg-linear-to-b',
        bottom && 'bg-linear-to-t',
        className
      )}
    />
  );
}
interface SessionViewProps {
  appConfig: LivekitConfig;
}

export const SessionView = ({
  appConfig,
  ...props
}: React.ComponentProps<'div'> & SessionViewProps) => {
  useConnectionTimeout(200_000);
  useDebugMode({ enabled: IN_DEVELOPMENT });

  const messages = useChatMessages();
  const chatOpen = useLivekitUiStore(state => state.chatOpen);
  const resetLivekitUi = useLivekitUiStore(state => state.reset);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  const controls: ControlBarControls = {
    leave: true,
    microphone: true,
    camera: false,
    screenShare: false,
  };

  useEffect(() => {
    const lastMessage = messages.at(-1);
    const lastMessageIsLocal = lastMessage?.from?.isLocal === true;

    if (scrollAreaRef.current && lastMessageIsLocal) {
      scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    return () => {
      resetLivekitUi();
    };
  }, [resetLivekitUi]);

  return (
    <>
      <div
        className="flex flex-col gap-4 py-0 w-full relative z-10 h-full w-full overflow-y-auto"
        {...props}
      >
        {/* Chat Transcript */}
        <div
          className={cn(
            'inset-0 grid grid-cols-1 grid-rows-1 transcripts space-y-2',
            !chatOpen && 'pointer-events-none'
          )}
        >
          <Fade top className="absolute inset-x-4 top-0" />
          <ScrollArea ref={scrollAreaRef} className="px-4 md:px-6 h-full">
            <ChatTranscript
              hidden={!chatOpen}
              messages={messages}
              className="mx-auto max-w-2xl space-y-3 transition-opacity duration-300 ease-out h-full"
            />
          </ScrollArea>
        </div>
      </div>

      <SheetFooter className="sticky bottom-0 border-t p-4 flex flex-row gap-2">
        <MotionBottom
          {...BOTTOM_VIEW_MOTION_PROPS}
          className="inset-x-3 bottom-0 z-50 md:inset-x-12 w-full"
        >
          <PreConnectMessage messages={messages} className="pb-4" />
          <div className="bg-background relative">
            <AgentControlBar controls={controls} />
          </div>
        </MotionBottom>
      </SheetFooter>
    </>
  );
};
